/// <amd-module name="Build/Common/Library/Navigation" />
import { IBuildLinkingService } from "Build/Client/Linking";

import { Action } from "Build/Flux/Action";

import { IVssService, Services, VssService, IVssPageContext } from "VSS/Platform/Context";
import { IVssHistoryService, HistoryServiceEventNames, INavigationStateChangedEvent } from "VSS/Platform/History";

export type StateType = { [key: string]: string };

export interface IBuildNavigationService extends IVssService {
    getCurrentState(): { [key: string]: string };
    getHistoryService(): IVssHistoryService;
    getLinkingService(): IBuildLinkingService;
    pushState(state: { [key: string]: string }): void;
    subscribeToNavigationChanges(actionHub: NavigationActionHub): void;
}

export function openLinkSecurely(link: string, inNewWindow: boolean = false) {
    const target = inNewWindow ? "_blank" : "_self";
    const openedWindow = window.open(link, target);
    if (openedWindow) {
        openedWindow.opener = null;
    }
}

export class NavigationActionHub {
    private _navigationChanged: Action<INavigationStateChangedEvent>;

    constructor() {
        this._navigationChanged = new Action<INavigationStateChangedEvent>();
    }

    public get navigationChanged(): Action<INavigationStateChangedEvent> {
        return this._navigationChanged;
    }
}

class BuildNavigationService extends VssService implements IBuildNavigationService {
    private _actionHub: NavigationActionHub | undefined;
    private _historyService: IVssHistoryService | undefined;
    private _linkingService: IBuildLinkingService | undefined;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this._historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this._historyService.subscribe(this._onNavigate, HistoryServiceEventNames.statePopped);

        this._linkingService = this.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        super._serviceEnd(pageContext);

        this._historyService && this._historyService.unsubscribe(this._onNavigate, HistoryServiceEventNames.statePopped);
    }

    public subscribeToNavigationChanges(actionHub: NavigationActionHub) {
        this._actionHub = actionHub;
    }

    public getCurrentState(): { [key: string]: string } {
        return (this._historyService && this._historyService.getState()) || {};
    }

    public getHistoryService() {
        return this._historyService as IVssHistoryService;
    }

    public getLinkingService() {
        return this._linkingService as IBuildLinkingService;
    }

    public pushState(state: StateType) {
        if (this._historyService) {
            let currentState = this._historyService.getState();
            Object.keys(state).forEach((key) => {
                currentState[key] = state[key];
            });

            this._historyService.pushState(currentState);
        }
    }

    private _onNavigate = (event: INavigationStateChangedEvent) => {
        if (this._actionHub) {
            this._actionHub.navigationChanged.invoke(event);
        }
    }
}

Services.add("IBuildNavigationService", { serviceFactory: BuildNavigationService });
